import React, { useEffect, useRef, useState, useMemo } from 'react';
import Pagination from '../components/Pagination';
import API from '../api';
import './products.css'; // << use the sample theme/colors
import productExampleCsv from '../assets/products_example.csv';
import categoryExampleCsv from '../assets/categories_example.csv';

function Products(){
  // --- auth / role ---
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';
  const businessId = isAdmin ? (user?.user?.id || user?.id) : localStorage.getItem('id');

  // --- data ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [inventory, setInventory] = useState({});

  // --- product modal ---
  const [showProduct, setShowProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productForm, setProductForm] = useState({ name:'', unit:'', price_per_unit:'', category:'' });

  // --- category modal ---
  const [showCategory, setShowCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name:'', description:'' });

  const [prodSearch, setProdSearch]         = useState('');
  const [catSearch, setCatSearch]           = useState('');
  const [prodPage, setProdPage]             = useState(1);
  const [prodPageSize, setProdPageSize]     = useState(10);
  const [catPage, setCatPage]               = useState(1);
  const [catPageSize, setCatPageSize]       = useState(10);

  // --- CSV modal ---
  const [showCsv, setShowCsv] = useState(false);
  const [csvType, setCsvType] = useState('products'); // 'products' | 'categories'
  const [csvFile, setCsvFile] = useState(null);
  const dzInputRef = useRef(null);

  // ===== Effects =====
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchInventoryStatus();
  }, []);

  const fetchProducts = async () => {
    try{
      const res = await API.get(`/auth/products/${businessId}`);
      setProducts(res.data.products || []);
    }catch(err){
      const msg = err?.response?.data?.message || '';
      if(msg.includes('No products')) setProducts([]);
      else console.error(err);
    }
  };

  const fetchCategories = async () => {
    try{
      const res = await API.get(`/auth/categories/${businessId}`);
      setCategories(res.data.categories || []);
    }catch(err){
      const msg = err?.response?.data?.message || '';
      if(msg.includes('No categories')) setCategories([]);
      else console.error(err);
    }
  };

  const fetchInventoryStatus = async () => {
    try{
      const res = await API.get(`/auth/inventory/status/${businessId}`);
      setInventoryEnabled(Boolean(res.data.inventoryEnabled));
    }catch(err){ console.error(err); }
  };

  // sync inventory on toggle / products change
  useEffect(() => {
    const sync = async () => {
      try{
        const productIds = products.map(p => p._id);
        const res = await API.post(`/auth/inventory/sync`, { businessId, productIds, defaultQuantity: 1 });
        const map = {};
        (res.data.inventory || []).forEach(i => { map[i.product] = i.quantity_in_stock; });
        setInventory(map);
      }catch(e){ console.error(e); }
    };
    if(inventoryEnabled && products.length){ sync(); }
  }, [inventoryEnabled, products]);

  // ===== Handlers =====
  const toggleInventory = async () => {
    try{
      const next = !inventoryEnabled;
      await API.put(`/auth/inventory/status/${businessId}`, { inventoryEnabled: next });
      setInventoryEnabled(next);
    }catch(err){ alert('Failed to toggle inventory'); }
  };

  // Product CRUD
  const openAddProduct = () => { setEditingProductId(null); setProductForm({name:'',unit:'',price_per_unit:'',category:''}); setShowProduct(true); };
  const openEditProduct = p => {
    setEditingProductId(p._id);
    setProductForm({ name:p.name, unit:p.unit, price_per_unit:p.price_per_unit, category: p.category?._id || '' });
    setShowProduct(true);
  };
  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...productForm, business_id: businessId };
    try{
      if(editingProductId) await API.put(`/auth/products/${editingProductId}`, payload);
      else                 await API.post(`/auth/products`, payload);
      await fetchProducts(); setShowProduct(false);
    }catch(err){ alert('Failed to save product'); }
  };
  const deleteProduct = async (id) => {
    if(!window.confirm('Delete this product?')) return;
    await API.delete(`/auth/products/${id}`); fetchProducts();
  };

  // Category CRUD
  const openAddCategory = () => { setEditingCategoryId(null); setCategoryForm({name:'',description:''}); setShowCategory(true); };
  const openEditCategory = c => { setEditingCategoryId(c._id); setCategoryForm({name:c.name, description:c.description}); setShowCategory(true); };
  const saveCategory = async (e) => {
    e.preventDefault();
    const payload = { ...categoryForm, business_id: businessId };
    try{
      if(editingCategoryId) await API.put(`/auth/categories/${editingCategoryId}`, payload);
      else                 await API.post(`/auth/categories`, payload);
      await fetchCategories(); setShowCategory(false);
    }catch(err){ alert('Failed to save category'); }
  };
  const deleteCategory = async (id) => {
    if(!window.confirm('Delete this category?')) return;
    await API.delete(`/auth/categories/${id}`); fetchCategories();
  };

  // Inventory
  const changeQty = async (productId, delta) => {
    const next = (inventory[productId] || 0) + delta;
    if(next < 0) return;
    try{
      await API.put(`/auth/inventory`, { businessId, productId, quantity: next });
      setInventory(prev => ({ ...prev, [productId]: next }));
    }catch(err){ alert('Failed to update quantity'); }
  };

  // CSV
  const openCsvModal = (type) => { setCsvType(type); setCsvFile(null); setShowCsv(true); };
  const uploadCsv = async () => {
    if(!csvFile) return alert('Please choose a CSV file.');
    const fd = new FormData();
    fd.append('file', csvFile);
    fd.append('type', csvType);
    fd.append('business_id', businessId);
    try{
      await API.post('/auth/uploadCsv', fd, { headers:{'Content-Type':'multipart/form-data'} });
      alert('Upload successful!');
      setShowCsv(false);
      fetchProducts(); fetchCategories();
    }catch(err){ alert('Upload failed'); }
  };

  const filteredProducts = useMemo(() => products.filter(p =>
    p.name?.toLowerCase().includes(prodSearch.toLowerCase()) ||
    (p.category?.name || '').toLowerCase().includes(prodSearch.toLowerCase())
  ), [products, prodSearch]);
  useEffect(() => setProdPage(1), [prodSearch]);
  const paginatedProducts = useMemo(() =>
    filteredProducts.slice((prodPage - 1) * prodPageSize, prodPage * prodPageSize),
  [filteredProducts, prodPage, prodPageSize]);

  const filteredCategories = useMemo(() => categories.filter(c =>
    c.name?.toLowerCase().includes(catSearch.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(catSearch.toLowerCase())
  ), [categories, catSearch]);
  useEffect(() => setCatPage(1), [catSearch]);
  const paginatedCategories = useMemo(() =>
    filteredCategories.slice((catPage - 1) * catPageSize, catPage * catPageSize),
  [filteredCategories, catPage, catPageSize]);

  // ===== Render =====
  return (
    <div className="services-page">
      {/* Header / Actions */}
      <div className="sp-header">
        <div className="sp-header-left">
          <h1 className="sp-title">Products <span className="sp-count">{products.length}</span></h1>
          <input
            className="sp-search"
            type="text"
            placeholder="Search products…"
            value={prodSearch}
            onChange={e => setProdSearch(e.target.value)}
          />
        </div>
        {isAdmin && (
          <div className="sp-actions">
            <button className="sp-btn sp-btn-ghost" onClick={toggleInventory}>
              {inventoryEnabled ? 'Disable Inventory Management' : 'Enable Inventory Management'}
            </button>
            <button className="sp-btn sp-btn-primary" onClick={openAddProduct}>+ Add Product</button>
            <button className="sp-btn sp-btn-primary" onClick={openAddCategory}>+ Add Category</button>
            <button className="sp-btn sp-btn-lime" onClick={()=>openCsvModal('products')}>Upload Products CSV</button>
            <button className="sp-btn sp-btn-lime" onClick={()=>openCsvModal('categories')}>Upload Categories CSV</button>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Unit</th>
              <th>Price / Unit($)</th>
              <th>Category</th>
              {isAdmin && <th className="sp-actions-col">Actions</th>}
              {inventoryEnabled && <th style={{width:120}}>Quantity</th>}
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 && (
              <tr><td colSpan={inventoryEnabled ? 6 : 5} className="sp-empty">
                {prodSearch ? 'No products match your search.' : 'No products'}
              </td></tr>
            )}
            {paginatedProducts.map(p => (
              <tr key={p._id}>
                <td className="sp-name-cell">{p.name}</td>
                <td><span className="sp-badge sp-badge--unit">{p.unit}</span></td>
                <td><span className="sp-badge sp-badge--price">${parseFloat(p.price_per_unit || 0).toFixed(2)}</span></td>
                <td>{p.category?.name ? <span className="sp-badge sp-badge--category">{p.category.name}</span> : <span className="sp-muted">—</span>}</td>
                {isAdmin && (
                  <td>
                    <div className="sp-row-actions">
                      <button className="sp-icon-btn" onClick={()=>openEditProduct(p)} title="Edit">✎</button>
                      <button className="sp-icon-btn sp-icon-danger" onClick={()=>deleteProduct(p._id)} title="Delete">🗑</button>
                    </div>
                  </td>
                )}
                {inventoryEnabled && (
                  <td>
                    <div className="sp-qty">
                      <button className="sp-qty-btn" onClick={()=>changeQty(p._id,-1)}>−</button>
                      <span className="count">{inventory[p._id] ?? 0}</span>
                      <button className="sp-qty-btn" onClick={()=>changeQty(p._id, 1)}>+</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={prodPage}
          totalPages={Math.max(1, Math.ceil(filteredProducts.length / prodPageSize))}
          totalItems={filteredProducts.length}
          pageSize={prodPageSize}
          onPageChange={setProdPage}
          onPageSizeChange={(s) => { setProdPageSize(s); setProdPage(1); }}
        />
      </div>

      {/* Categories */}
      <div style={{height:18}} />
      <div className="sp-header" style={{marginBottom:12}}>
        <div className="sp-header-left">
          <h3 className="sp-title" style={{fontSize:18, margin:0}}>Categories <span className="sp-count">{categories.length}</span></h3>
          <input
            className="sp-search"
            type="text"
            placeholder="Search categories…"
            value={catSearch}
            onChange={e => setCatSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              {isAdmin && <th className="sp-actions-col">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 && (
              <tr><td colSpan={isAdmin ? 3 : 2} className="sp-empty">
                {catSearch ? 'No categories match your search.' : 'No categories'}
              </td></tr>
            )}
            {paginatedCategories.map(c => (
              <tr key={c._id}>
                {/* <td className="sp-desc-cell">{c._id}</td> */}
                <td>{c.name}</td>
                <td className="sp-desc-cell">{c.description}</td>
                {isAdmin && (
                  <td>
                    <div className="sp-row-actions">
                      <button className="sp-icon-btn" onClick={()=>openEditCategory(c)} title="Edit">✎</button>
                      <button className="sp-icon-btn sp-icon-danger" onClick={()=>deleteCategory(c._id)} title="Delete">🗑</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={catPage}
          totalPages={Math.max(1, Math.ceil(filteredCategories.length / catPageSize))}
          totalItems={filteredCategories.length}
          pageSize={catPageSize}
          onPageChange={setCatPage}
          onPageSizeChange={(s) => { setCatPageSize(s); setCatPage(1); }}
        />
      </div>

      {/* ===== Product Modal ===== */}
      {showProduct && (
        <div className="sp-modal-overlay" onClick={()=>setShowProduct(false)}>
          <div className="sp-modal" onClick={e=>e.stopPropagation()}>
            <div className="sp-modal-header">
             
              <h3>{editingProductId ? 'Edit Product' : 'Add New Product'}</h3>
               <button className="sp-modal-close" onClick={()=>setShowProduct(false)}>×</button>
            </div>
            <form onSubmit={saveProduct}>
              <label className="sp-label">Name</label>
              <input className="sp-input" value={productForm.name} onChange={e=>setProductForm({...productForm, name:e.target.value})} required/>

              <label className="sp-label">Unit</label>
              <input className="sp-input" value={productForm.unit} onChange={e=>setProductForm({...productForm, unit:e.target.value})} required/>

              <label className="sp-label">Price per Unit</label>
              <input className="sp-input" type="number" step="0.01" value={productForm.price_per_unit} onChange={e=>setProductForm({...productForm, price_per_unit:e.target.value})} required/>

              <label className="sp-label">Category</label>
              <select className="sp-input" value={productForm.category} onChange={e=>setProductForm({...productForm, category:e.target.value})} required>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>

              <div className="sp-modal-actions">
                <button type="button" className="sp-btn sp-btn-light" onClick={()=>setShowProduct(false)}>Cancel</button>
                <button type="submit" className="sp-btn sp-pop-up-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== Category Modal ===== */}
      {showCategory && (
        <div className="sp-modal-overlay" onClick={()=>setShowCategory(false)}>
          <div className="sp-modal" onClick={e=>e.stopPropagation()}>
            <div className="sp-modal-header">
              
              <h3>{editingCategoryId ? 'Edit Category' : 'Add New Category'}</h3>
              <button className="sp-modal-close" onClick={()=>setShowCategory(false)}>×</button>
            </div>
            <form onSubmit={saveCategory}>
              <label className="sp-label">Name</label>
              <input className="sp-input" value={categoryForm.name} onChange={e=>setCategoryForm({...categoryForm, name:e.target.value})} required/>

              <label className="sp-label">Description</label>
              <input className="sp-input" value={categoryForm.description} onChange={e=>setCategoryForm({...categoryForm, description:e.target.value})} />

              <div className="sp-modal-actions">
                <button type="button" className="sp-btn sp-btn-light" onClick={()=>setShowCategory(false)}>Cancel</button>
                <button type="submit" className="sp-btn sp-pop-up-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== CSV Modal ===== */}
      {showCsv && (
        <div className="sp-modal-overlay" onClick={()=>setShowCsv(false)}>
          <div className="sp-modal" onClick={e=>e.stopPropagation()}>
            <div className="sp-modal-header">
              
              <h3>Upload {csvType === 'products' ? 'Products' : 'Categories'} CSV</h3>
              <button className="sp-modal-close" onClick={()=>setShowCsv(false)}>×</button>
            </div>
            <div>
              <div style={{fontSize:13, color:'#334155', margin:'8px 0'}}>Your Business ID : <strong>{businessId}</strong></div>

              <div className="sp-dropzone" onClick={()=>dzInputRef.current?.click()}>
                <div className="sp-cloud">☁️</div>
                Drag & drop your file here or<span className="sp-browse"> Browse files</span>
                <input ref={dzInputRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>setCsvFile(e.target.files?.[0] || null)} />
                <div className="sp-file-name">{csvFile ? `Selected: ${csvFile.name}` : 'Only .csv files are supported.'}</div>
              </div>

              <a className="sp-example" href={csvType === 'products' ? productExampleCsv : categoryExampleCsv} download>
                Download Example CSV
              </a>

              <div className="sp-modal-actions">
                <button type="button" className="sp-btn sp-btn-light" onClick={()=>setShowCsv(false)}>Cancel</button>
                <button type="button" className="sp-btn sp-pop-up-btn-primary" onClick={uploadCsv}>Upload</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
